import { EMPTY_GUID, encode, getAllVersionsSorted, hasAttribute, widthValidation } from "@utils/helpers";
import { StatusCode } from "@utils/optionsSets/OptionSets";
import { currentWorkspaceId } from "index";
import { get } from "lodash";
import { createSelector } from "reselect";
import { InformationType, UserType } from "utils/optionsSets/OptionSets";
const getWidthValidation = (item) => widthValidation(item.width);

export const getAllPersonas = (state) => state.personas.all_personas;
export const getCjmInteractions = (state) => state.maps.current_map?.cjmInteractions;
export const getCjmContactData = (state) => state.maps.current_map?.cjmContactData;
export const getExperienceData = (state) => state.maps.current_map?.cjmStagesCustomerExperienceData;
export const getAllTouchpoints = (state) => state.libraries.touchPoint;
const getStageData = (state) => state.maps.current_map?.cjmStagesData;
export const getPhasesData = (state) => state.maps.current_map?.cjmPhasesData;

export const personaDataSelector = (personaId) => {
	return createSelector([getAllPersonas], (allPersonas) => allPersonas.find((p) => p.personaId === personaId));
};
export const getAllMemoizedInteractions = createSelector([getCjmInteractions], (cjmInteractions) => cjmInteractions);
export const getInteractionSelector = (personaId, stageId, itemId, channelId) => {
	return createSelector(
		[getCjmInteractions],
		(cjmInteractions) => {
			const personaInteractions = cjmInteractions?.[personaId];

			if (!stageId) {
				return personaInteractions || {}; // Return same object reference if unchanged
			}
			if(channelId){
				return personaInteractions?.[stageId]?.find(interaction => interaction.channelId === channelId) || {}
			}
			if (!itemId) {
				return personaInteractions?.[stageId]?.sort((a, b) => a?.order - b?.order) || []; // Return same array reference if unchanged
			}
			return personaInteractions?.[stageId]?.find(interaction => interaction.id === itemId) || {}
		}
	);
};
export const getCjmExperienceData = (personaId, stageId) => {
	return createSelector([getExperienceData], (experienceData) => experienceData?.[personaId]?.[stageId] || {});
};
export const getCjmExperienceDataDiscussions = (personaId, stageId, type) => {
	return createSelector([getExperienceData], (experienceData) => {
		const data = experienceData?.[personaId]?.[stageId] || {};
		return type ==='pros' ? data.prosDiscussionIds : data.consDiscussionIds
	});
};

export const makeGetCjmContactData = (personaIdStr, stageId, touchpointId) => {
	if (touchpointId !== EMPTY_GUID) {
		return createSelector([getCjmContactData], (cjmContactData) => cjmContactData?.[personaIdStr]?.[stageId]?.[touchpointId] ?? []);
	}
	return createSelector([getCjmContactData], (cjmContactData) => cjmContactData?.[personaIdStr]?.[stageId] ?? []);
};

export const makeGetTouchpoint = (touchpointId) => {
	return createSelector([getAllTouchpoints], (touchPoints) => touchPoints?.find((touchPoint) => touchPoint.id === touchpointId));
};
export const makeFindStageIndex = (stageId) => {
	return createSelector([getStageData], (stages) => stages?.findIndex((stage) => stage.id === stageId));
};
export const getStage = (stageId) => {
	return createSelector([getStageData], (stages) => stages?.find((stage) => stage.id === stageId));
};
export const getPhase = (phaseId) => {
	return createSelector([getPhasesData], (phases) => phases?.find((phase) => phase.id === phaseId));
};
export const getPhaseByStageId = (stageId) => {
	return createSelector([getStageData, getPhasesData], (stages, phases) => {
		const stage = stages?.find((stage) => stage.id === stageId);
		return phases?.find((phase) => phase.id === stage?.phase.id);
	});
};

export const getShowContactsSentimentLevel = (state) => state.maps.current_map.showContactsSentimentLevel;
export const getShowPersonaSentimentLevel = (state) => state.maps.current_map.showPersonaSentimentLevel;
export const cjmPersonasData = (state) => state.maps.current_map?.cjmPersonasData;
export const getToken = (state) => state.auth.token;
export const mapCurrentUser = (state) => state.maps.currentUser;
export const getMapLegend = (state) => state.maps.current_map?.mapLegend || [];
export const getCjmStagesData = (state) => state.maps.current_map?.cjmStagesData;
export const getCjmPersonasData = (state) => state.maps.current_map?.cjmPersonasData;
export const getChart = (state) => state.maps.current_map?.chart || {};
export const getTargetChart = (state) => state.maps.current_map.chart?.feelingPoints_Target || [];
export const getShowTargetValues = (state) => state.maps.current_map.showTargetValues;
export const getCjmCxGoals = (state) => state.maps.current_map?.cjmCxGoals || {};
export const getMeasures = (state) => state.libraries?.measures || [];
export const getTimeLines = (state) => state.maps.current_map?.cjmTimeLines || {};
export const getCjmSwimlanes = (state) => state.maps.current_map?.cjmSwimLanes || [];
export const getCjmDiscussions = (state) => state.maps.current_map?.cjmDiscussions || {};
export const getCjmCustomerFeedback = (state) => state.maps.current_map?.cjmCustomerFeedback || [];
export const getSessionId = (state) => encode(state?.auth?.userInfo?.SessionId || "");
export const gelAllTouchpoints = (state) => state.libraries.touchPoint;
export const getUserInfo = (state) => state?.auth?.userInfo;


export const allMemoizedTouchpoints = createSelector([gelAllTouchpoints], (touchPoints) => touchPoints);

export const getStageTimeLine = (stageId) => {
	return createSelector([getTimeLines], (timeLines) => timeLines?.[stageId]?.[0] || false);
};

export const getStages = createSelector([getCjmStagesData], (cjmStagesData) => cjmStagesData);
export const getPhases = createSelector([getPhasesData], (cjmPhasesData) => cjmPhasesData);

export const memoizedSwimlanes = createSelector([getCjmSwimlanes], (cjmSwimLanes) => cjmSwimLanes || []);
export const memoizedSwimlanesRelevantCount = createSelector(
	[getCjmSwimlanes],
	(cjmSwimLanes) => cjmSwimLanes?.filter((swimlane) => !["StageHeaders", "PhaseHeaders"].includes(swimlane.id))?.length || 0
);

export const getSwimlane = (swimlanekey) => {
	return createSelector([getCjmSwimlanes], (getCjmSwimlanes) => getCjmSwimlanes?.find((swimlane) => swimlane.key === swimlanekey));
};
export const getStageCustomerFeedback = (stageId, personaId) => {
	return createSelector([getCjmCustomerFeedback], (customerFeedback) => customerFeedback?.[personaId]?.[stageId] || {});
};
export const getCustomerFeedbackStagesIds = (stageId, personaId) => {
	return createSelector([getCjmCustomerFeedback], (customerFeedback) => customerFeedback?.[personaId]?.[stageId]?.map((int) => int.id) || []);
};
export const getRelevantPersonas = createSelector([getCjmPersonasData, getAllPersonas], (cjmPersonasData, allPersonas) =>
	cjmPersonasData?.map((person) => allPersonas?.find((persona) => persona.personaId === person.personaId))
);

export const getTotalWidth = createSelector([getCjmStagesData], (cjmStagesData) => cjmStagesData?.map(getWidthValidation)?.reduce((a, b) => a + b, 0) || 0);



export const memoizedCxGoals = createSelector([getCjmCxGoals], (cjmCxGoals) => cjmCxGoals)
export const makeGetCjmGoalsFormated = createSelector([getCjmCxGoals, getCjmStagesData], (cjmCxGoals, cjmStagesData) => {
	return cjmStagesData?.map((stage, index) => (!hasAttribute(cjmCxGoals, stage.id) ? [] : cjmCxGoals[stage.id]));
});
export const getMeasureById = (measureId) => {
	return createSelector([getMeasures], (measures) => measures?.find((measure) => measure.id === measureId));
};
export const findCxGoal = (stageId, cxGoalId) => {
	return createSelector([getCjmCxGoals], (cjmCxGoals) => cjmCxGoals?.[stageId]?.find((cxGoal) => cxGoal.id === cxGoalId));
};


export const getCurrentMapPersonaIds = createSelector([getCjmPersonasData], (personas) => personas?.map((p) => p.personaId));

// cxPages
export const getAllSolutions = (state) => state.solutions.all_solutions;
export const getAllOpportunities = (state) => state?.opportunity?.all_Opportunities;
export const getAllCxActions = (state) => state.actions.all_actions;

export const getAllMaps = (state) => state.maps.all_maps;
export const getAllPriorities = (state) => state.libraries?.priority;
export const getAllStatus = (state) => state.libraries?.status;
export const getAllLibraries = (state) => state.libraries;
export const getAllProgramsSelector = (state) => state.cxProgram.all_cxPrograms;
export const getshowSampleData = (state) => state.settings.organization?.showSampleData;

export const getAllCxProgramAssessments = (state) => state.cxProgram?.current_program?.cxAssessments;

export const getLibraries = createSelector([getAllLibraries], (libraries) => libraries);

export const getAllCustomLibraryItems = (libraryId) => {
	return createSelector([getAllLibraries], (libraries) => libraries?.customLibraries.find((cl) => cl.id === libraryId)?.values || []);
};

export const FindSolutionById = (solutionId) => {
	return createSelector([getAllSolutions], (allSolutions) => allSolutions?.find((solution) => solution.id === solutionId));
};
export const FindSolutionIndexById = (solutionId) => {
	return createSelector([retrieveAllActiveSolutions], (allSolutions) => allSolutions?.findIndex((solution) => solution.id === solutionId));
};
export const retrieveAllActiveSolutions = createSelector([getAllSolutions, getshowSampleData], (allSolutions, showSampleData) =>
	allSolutions?.filter((solution) => (showSampleData ? solution.statusCode === StatusCode.Active : solution.statusCode === StatusCode.Active && solution.isSample === false))
);
export const FindMapById = (mapId) => {
	return createSelector([getAllMaps], (allMaps) => allMaps?.find((map) => map.cjmId === mapId));
};
export const getAllMapsList = createSelector([getAllMaps], (allMaps) => allMaps);
export const findPriority = (attribute, priorityId) => {
	return createSelector([getAllPriorities], (all_priority) => all_priority?.[attribute]?.find((pt) => pt.id === priorityId));
};

export const FindOpportunityById = (opportunityId) => {
	return createSelector([getAllOpportunities], (allOpportunities) => allOpportunities?.find((opportunity) => opportunity.id === opportunityId));
};
export const FindOpportunityIndexById = (opportunityId) => {
	return createSelector([retrieveAllActiveOpportunities], (allOpportunities) => allOpportunities?.findIndex((opportunity) => opportunity.id === opportunityId));
};
export const retrieveAllActiveOpportunities = createSelector([getAllOpportunities, getshowSampleData], (allOpportunities, showSampleData) =>
	allOpportunities?.filter((opportunity) => (showSampleData ? opportunity.statusCode === StatusCode.Active : opportunity.statusCode === StatusCode.Active && opportunity.isSample === false))
);
export const FindActionById = (actionId) => {
	return createSelector([getAllCxActions], (allActions) => allActions?.find((action) => action.id === actionId));
};
export const FindActionIndexById = (actionId) => {
	return createSelector([retrieveAllActiveActions], (allActions) => allActions?.findIndex((action) => action.id === actionId));
};
export const retrieveAllActiveActions = createSelector([getAllCxActions, getshowSampleData], (allActions, showSampleData) =>
	allActions?.filter((action) => (showSampleData ? action.statusCode === StatusCode.Active : action.statusCode === StatusCode.Active && action.isSample === false))
);
export const FindStatusById = (attribute, statusId) => {
	return createSelector([getAllStatus], (all_status) => all_status?.[attribute]?.find((pt) => pt.id === statusId));
};

export const FindAssessmentById = (id) => {
	return createSelector([getAllCxProgramAssessments], (assessments) => assessments?.find((assessment) => assessment.id === id));
};

// solutiosn

export const getCjmSolutions = (state) => state.maps.current_map.cjmSolutions;
export const getFolders = (state) => state.folders;

export const getFolderById = createSelector(
	(state) => state.folders,
	(_, folderId) => folderId,
	(folders, folderId) => folders?.find((folder) => folder.id === folderId) || {}
);

export const getCurrentProject = (state) => state.projects.current_project || {};

export const projectBusinessCase = createSelector([getCurrentProject], (current_project) => ({
	id: current_project.id,
	authorizedUsers: current_project.authorizedUsers,
	attributesList: current_project.attributesList,
}));

export const projectDetails = createSelector([getCurrentProject], (current_project) => ({
	id: current_project.id,
	projectManagementToolLink: current_project.projectManagementToolLink,
	projectManagementTool: current_project.projectManagementTool,
}));

export const projectFinancialImpact = createSelector([getCurrentProject], (current_project) => ({
	id: current_project.id,
	financialImpact: current_project.financialImpact,
	attributesList: current_project.attributesList,
	currencySymbol: current_project.currencySymbol,
	userProvided: current_project.userProvided,
}));

//projects

export const getAllProjects = (state) => state.projects.all_projects;

export const FindProjectById = (id) => {
	return createSelector([getAllProjects], (allProjects) => allProjects?.find((project) => project.id === id));
};

export const getMapVersionsSorted = ({ currentMapId }) => {
	return createSelector([getAllMaps], (allMaps) => {
		const currentMap = allMaps.find((map) => map.cjmId === currentMapId);
		const parentMapId = currentMap?.parentMapId;
		const allVersionsSorted = getAllVersionsSorted({ allMaps, currentMapId, parentMapId });
		return allVersionsSorted;
	});
};
export const getTeamsUsers = createSelector(
	(state) => ({ authorizedUsers: state.settings?.authorizedUsers, users: state.settings?.users }),
	(_, teamUsers) => teamUsers,
	({ authorizedUsers, users }, teamUsers) => {
		return authorizedUsers?.filter((user) => teamUsers?.includes(user.id) && users.find((u) => u.loginName.toLowerCase() === user?.domainName.toLowerCase())?.isActive) || [];
	}
);

export const getAllTags = (state) => state.libraries.tags;
export const getAllGroups = (state) => state.libraries.group;
const selectLibraries = (state) => state.libraries;
const selectUiTableEntity = (state, entity, context) => get(state, ["ui", "table", "entity", entity, context]);
const selectUserSetting = (state, entity) => get(state, ["settings", "userSetting", "savedViewsByEntity", entity]);

export const selectGroup = createSelector([selectLibraries], (libraries) => libraries?.group?.FormatFilterArray());

export const selectTag = createSelector([selectLibraries], (libraries) => libraries?.tags?.FormatFilterArray());
export const selectPriorities = ({ entity }) => createSelector(selectLibraries, (libraries) => libraries?.priority?.[entity]?.FormatFilterArray());
export const selectStatus = ({ entity }) => createSelector(selectLibraries, (libraries) => libraries?.status?.[entity]?.FormatFilterArray());
export const getAllStatusByEntity = ({ entity }) => createSelector(selectLibraries, (libraries) => libraries?.status?.[entity] || []);
export const getAllPriorityByEntity = ({ entity }) => createSelector(selectLibraries, (libraries) => libraries?.priority?.[entity] || []);

export const selectSelectedViewId = createSelector([selectUiTableEntity], (uiTableEntity) => uiTableEntity?.selectedViewId);
export const getCjmSwimlaneDetails = (state) => state.maps.current_map?.cjmSwimLaneDetails;
export const selectQueryView = createSelector([selectUserSetting], (userSetting) => userSetting);
export const getCjmSwimlaneDetailsById = (swimlaneId) => createSelector([getCjmSwimlaneDetails], (cjmSwimLaneDetails) => cjmSwimLaneDetails?.[swimlaneId] || {})

export const getSwimlaneDetails = (swimlaneId, stageId) => {
	return createSelector([getCjmSwimlaneDetails], (cjmSwimLaneDetails) => {
		// console.log(cjmSwimLaneDetails[swimlaneId][stageId],cjmSwimLaneDetails,swimlaneId,stageId);
		return cjmSwimLaneDetails?.[swimlaneId]?.[stageId] || {};
	});
};


const getSelectedIds = (state) => state?.ui?.table?.selectedIds || [];
export const getSelectedIdsMemoized = createSelector([getSelectedIds], (selectedIds) => selectedIds);

export const getInsightById = (id) => {
	return createSelector([getAllInsights], (allInsights) => {
		const insight = allInsights?.find((insight) => insight.id === id);
		return insight;
	});
};

//Insights
export const getAllInsights = (state) => state.insights?.all_insights || [];

export const getInsightPersonaIds = (id) => {
	return createSelector([getAllInsights], (allInsights) => allInsights?.find((insight) => insight.id === id)?.linkedPersonas);
};

//Analytics
export const analyticsOriginalDataSelector = (state) => state.analytics.originalData;

// requests
export const getAllMutations = (state) => state.requests.mutations;
export const getRequestStatus = ({ type }) => createSelector([getAllMutations], (requests) => requests?.[type] || {});

export const getAllQueries = (state) => state.requests.queries;
export const getQueriesStatus = ({ type }) => createSelector([getAllQueries], (requests) => requests?.[type] || {});

//Workspaces
export const getAllWorkspaces = (state) => state?.workspaces?.all_workspaces || [];
export const getAllActiveWorkspaces = (state) => state?.workspaces?.all_workspaces.filter((ws) => ws.statusCode === 1) || [];
export const findWorkspaceById = (id) => {
	return createSelector([getAllWorkspaces], (allWorkspaces) => allWorkspaces?.find((workspace) => workspace.id == id));
};
export const getAllActiveWorkspacesMemoized = createSelector([getAllWorkspaces], (workspaces) => workspaces?.filter((ws) => ws.statusCode === 1));

export const getCurrentWorkspace = createSelector([getAllWorkspaces], (workspaces) => workspaces.find((ws) => ws?.id === currentWorkspaceId));
export const isExistingWorkspaces = createSelector([getAllActiveWorkspaces], (workspaces) => workspaces?.length > 0);

export const getTeamsObject = (state) => state.settings?.teams || {};
export const getTeams = createSelector([getTeamsObject], (teamsDic) => Object.values(teamsDic)?.map((team) => ({ ...team, fullName: team.name })) || []);


export const getMemoizedCjmStagesData = createSelector([getCjmStagesData], (cjmStagesData) => cjmStagesData);

const getMainRoute = (state) => state.maps.current_map.showMainRoute;
const getAlternativeRoute = (state) => state.maps.current_map.showAlternativeRoute;

export const getMainAlternativeRoutes = createSelector([getMainRoute, getAlternativeRoute], (showMainRoute, showAlternativeRoute) => ({ showMainRoute, showAlternativeRoute }));

export const getPendingGetRequestsCount = (state) => {
	const queries = state.requests.queries;
	let pendingCount = 0;

	for (const key in queries) {
		if (queries[key].networkStatus === "loading" && queries[key].request.method === "GET") {
			pendingCount++;
		}
	}

	return pendingCount;
};
export const getBottomSwimlanesPosition = (swimlaneKey) => {
	return createSelector([getCjmSwimlanes], (cjmSwimLanes) => {
		const index = cjmSwimLanes?.findIndex((swimlane) => swimlane.key === swimlaneKey);
		return index === cjmSwimLanes?.length - 3 || index === cjmSwimLanes?.length - 2 || index === cjmSwimLanes?.length - 1;
	});
};
export const mapUsers = (state) => state.maps?.current_linked_users || [];
export const getCurrentMapUsers = createSelector([mapUsers], (users) => users);

const currentMap = (state) => state.maps.current_map;
export const allInsightTypes = (state) => state.libraries.insightTypes;

export const getStagesDetailsonInsights = (stageId, insightId) => {
	return createSelector(
		[getAllInsights, currentMap, getStageData, getAllTouchpoints, allInsightTypes, getAllOpportunities, getAllSolutions],
		(allInsights, currentMap, stages, allTouchpoints, allInsightType, allOpportunities, allSolutions) => {
			const mapName = currentMap?.cjmName;

			const mapDescription = currentMap?.cjmDescription;

			const stageIndex = stages?.findIndex((stage) => stage.id === stageId);

			const stageName = stages?.[stageIndex]?.name;

			const stageInteractions = currentMap?.cjmData?.[stageIndex]?.find((interaction) => interaction.linkedInsights?.includes(insightId));

			const touchpointName = allTouchpoints?.find((touchpoint) => touchpoint.id === stageInteractions?.touchPointId)?.name;

			const interactionDescription = stageInteractions?.actions;

			const insight = allInsights?.find((insight) => insight.id === insightId);

			const insightName = insight?.name;

			const insightType = allInsightType?.find((type) => type.id === insight?.typeId)?.name;

			// find opportunities in this stage and on this map.
			const cjmSwimLaneDetails = currentMap?.cjmSwimLaneDetails;
			const swimlaneOpportunity = currentMap?.cjmSwimLanes?.find((sw) => sw.type === InformationType.Opportunity);
			let relatedOpportunities = [];
			if (swimlaneOpportunity) {
				const relatedOppIds = cjmSwimLaneDetails?.[swimlaneOpportunity.key]?.[stageId]?.items?.map((i) => i.opportunityId) || [];
				relatedOpportunities = allOpportunities?.filter((opp) => relatedOppIds.includes(opp.id));
			}
			const swimlaneSolutions = currentMap?.cjmSwimLanes?.find((sw) => sw.type === InformationType.Solution);
			let relatedSolutions = [];
			if (swimlaneSolutions) {
				const relatedSolIds = cjmSwimLaneDetails?.[swimlaneSolutions.key]?.[stageId]?.items?.map((i) => i.solutionId) || [];
				relatedSolutions = allSolutions?.filter((opp) => relatedSolIds.includes(opp.id));
			}
			return { mapName, mapDescription, stageName, insightName, insightType, touchpointName, interactionDescription, relatedOpportunities, relatedSolutions };
		}
	);
};
const cache = new Map();

export const getStagesDetails = (stageId) => {
	return createSelector([
		getAllInsights,
		currentMap,
		getStageData,
		getAllTouchpoints,
		allInsightTypes,
		getAllOpportunities,
		getAllSolutions
	], (
		allInsights,
		currentMap,
		stages,
		allTouchpoints,
		allInsightTypes,
		allOpportunities,
		allSolutions
	) => {


		const { cjmName: mapName, cjmId, cjmDescription: mapDescription, cjmData, cjmSwimLaneDetails, cjmSwimLanes } = currentMap || {};

		const stage = stages?.find((stage) => stage.id === stageId);
		const stageName = stage?.name;
		const stageData = cjmData?.[stages?.indexOf(stage)] || [];

		const touchpointMap = new Map(allTouchpoints?.map(tp => [tp.id, tp.name]));
		const insightTypeMap = new Map(allInsightTypes?.map(type => [type.id, type.name]));

		const allRelatedTouchpoints = stageData
			.map(interaction => touchpointMap.get(interaction?.touchPointId))
			.filter(Boolean);

		const allRelatedInsights = allInsights
			?.filter(insight => insight?.linkedCjms?.[cjmId]?.some(link => link.stageId === stageId))
			.map(insight => ({
				name: insight.name,
				type: insightTypeMap.get(insight?.typeId)
			}))
			.filter(Boolean);

		const getRelatedItems = (type, allItems, key) => {
			const swimlane = cjmSwimLanes?.find(sw => sw.type === type);
			if (!swimlane) return [];

			const relatedIds = cjmSwimLaneDetails?.[swimlane.key]?.[stageId]?.items?.map(item => item[key]) || [];
			return allItems?.filter(item => relatedIds.includes(item.id) && item.name.trim() !== "");
		};

		const relatedOpportunities = getRelatedItems(InformationType.Opportunity, allOpportunities, 'opportunityId')?.map(item => item.name);
		const relatedSolutions = getRelatedItems(InformationType.Solution, allSolutions, 'solutionId')?.map(item => item.name);

		const result = {
			mapName,
			mapDescription,
			stageName,
			allRelatedInsights,
			allRelatedTouchpoints,
			relatedOpportunities,
			relatedSolutions,
			insightTypes: allInsightTypes.map(type => type.name),
		};


		return result;
	});
};
export const getSusbscriptionName = (state) => state.auth?.userInfo?.UserSubscriptionPlanName?.toLowerCase();

export const getMapAccess = createSelector([currentMap, getUserInfo],
	(currentMap, userInfo) => {
		const userType = userInfo.UserType;

		const isCompanyAdmin = userType === UserType.companyAdmin;
		if (isCompanyAdmin) return { auhtorized: true, label: "active" };

		if (currentMap.noAccess) return { auhtorized: false, label: "MAP_NO_ACCESS" };



		return { auhtorized: true, label: "active" };
	})

export const getMapStatusValidation = (personaIdStr) => {
	return createSelector([currentMap, getUserInfo],
		(currentMap, userInfo) => {


			const haveDataOnCjm = Object.keys(currentMap)?.length > 0;
			if (!haveDataOnCjm) return { activated: true, label: "ERROR_LOADING_MAP" };

			if (currentMap.statusCode === StatusCode.Archived) return { activated: false, label: "MAP_ARCHIVED" };
			if (currentMap.statusCode === StatusCode.Trashed) return { activated: false, label: "MAP_DELETED" };
			if (currentMap.cjmId === null) return { activated: false, label: "MAP_NOT_FOUND" };

			if (currentMap?.cjmPersonasData && !currentMap?.cjmPersonasData?.some(x => x.personaId === personaIdStr)) {
				return { activated: false, personaId: personaIdStr, label: "PERSONA_NOCONNECT" };
			}


			const isActive = currentMap.statusCode === StatusCode.Active || currentMap.statusCode === StatusCode.Revised;
			const activated = haveDataOnCjm && currentMap.cjmId !== null && isActive;

			return { activated, label: activated ? "active" : "MAP_INACTIVE" };
		})
}
const getAllRecentWork = (state) => state.settings?.recentWorks || [];
export const isRecentWorkById = (recordId) => {
	return createSelector([getAllRecentWork], (recentWork) => recentWork?.some((work) => work.recordId === recordId));
}